import { useEffect, useState } from "react";
import { Dialog, DialogActions, DialogContent, DialogTitle } from "@mui/material";

import VuiBox from "components/VuiBox";
import VuiButton from "components/VuiButton";
import VuiTypography from "components/VuiTypography";

import { FaExclamationCircle, FaTimes } from "react-icons/fa";

import colors from "assets/theme/base/colors";
import rgba from "assets/theme/functions/rgba";

import useCommon from "hooks/useCommon";

import { resetUpdateStatusState } from "store/slice/housekeepingSlice";

import { RESOURCE_STATE_OPTIONS, RESOURCE_STATES } from "../constants";

const availableResourceStates = RESOURCE_STATE_OPTIONS.filter(
  (status) => status.value !== RESOURCE_STATES.OUT_OF_ORDER
);

const { dark } = colors;

const UpdateRoomStatusDialog = ({ open, room, onClose, onUpdate }) => {
  const { STORE, DISPATCH } = useCommon();

  const { updateStatusLoading, updateStatusError } = STORE((state) => state.housekeeping);

  const [resourceState, setResourceState] = useState("");

  useEffect(() => {
    if (!open) return;

    setResourceState(room?.resourceState || "");

    DISPATCH(resetUpdateStatusState());
  }, [open, room, DISPATCH]);

  const handleClose = () => {
    if (updateStatusLoading) return;

    DISPATCH(resetUpdateStatusState());

    onClose?.();
  };

  const handleSubmit = async () => {
    if (
      !room?.id ||
      !resourceState ||
      updateStatusLoading ||
      resourceState === room?.resourceState
    ) {
      return;
    }

    await onUpdate?.({
      roomId: room.id,
      resourceState,
      version: room.version,
    });
  };

  return (
    <Dialog open={open} onClose={handleClose} fullWidth maxWidth="sm">
      <DialogTitle>
        <VuiBox display="flex" alignItems="center" justifyContent="space-between" gap={2}>
          <VuiBox>
            <VuiTypography variant="lg">Update Room State</VuiTypography>

            <VuiTypography
              variant="caption"
              sx={{
                display: "block",
                mt: 0.4,
                opacity: 0.65,
              }}
            >
              {room?.roomName || room?.roomNumber || "Selected room"}
            </VuiTypography>
          </VuiBox>

          <VuiButton
            size="small"
            variant="text"
            color="primary"
            onClick={handleClose}
            disabled={updateStatusLoading}
            sx={{
              minWidth: 34,
              width: 34,
              height: 34,
              px: 0,
              borderRadius: "10px",
            }}
          >
            <FaTimes size={13} />
          </VuiButton>
        </VuiBox>
      </DialogTitle>

      <DialogContent>
        <VuiTypography
          variant="caption"
          sx={{
            display: "block",
            mb: 1.5,
            opacity: 0.7,
          }}
        >
          Select the current physical housekeeping state.
        </VuiTypography>

        <VuiBox
          display="grid"
          gridTemplateColumns={{
            xs: "1fr",
            sm: "1fr 1fr",
          }}
          gap={1.2}
          mb={1}
        >
          {availableResourceStates.map((status) => {
            const Icon = status.icon;

            const isSelected = resourceState === status.value;

            return (
              <VuiBox
                key={status.value}
                onClick={() => {
                  if (updateStatusLoading) return;

                  setResourceState(status.value);
                }}
                display="flex"
                alignItems="center"
                gap={1.2}
                p={1.4}
                borderRadius="12px"
                sx={{
                  cursor: updateStatusLoading ? "not-allowed" : "pointer",

                  opacity: updateStatusLoading ? 0.65 : 1,

                  border: isSelected
                    ? `1px solid ${status.color}`
                    : `1px solid ${rgba(dark.main, 0.25)}`,

                  background: isSelected ? rgba(status.color, 0.12) : "transparent",

                  transition: "all 0.2s ease",

                  "&:hover": {
                    borderColor: updateStatusLoading ? undefined : status.color,

                    background: updateStatusLoading
                      ? undefined
                      : rgba(status.color, isSelected ? 0.12 : 0.06),
                  },
                }}
              >
                <VuiBox
                  width={36}
                  height={36}
                  borderRadius="10px"
                  display="flex"
                  alignItems="center"
                  justifyContent="center"
                  flexShrink={0}
                  sx={{
                    color: status.color,
                    background: rgba(status.color, 0.12),
                    border: `1px solid ${rgba(status.color, 0.25)}`,
                  }}
                >
                  {Icon && <Icon size={13} color={status.color} />}
                </VuiBox>

                <VuiBox minWidth={0}>
                  <VuiTypography
                    variant="caption"
                    fontWeight={isSelected ? "bold" : "medium"}
                    sx={{
                      display: "block",
                      color: isSelected ? status.color : "inherit",
                    }}
                  >
                    {status.label}
                  </VuiTypography>

                  {status.description && (
                    <VuiTypography
                      variant="caption"
                      sx={{
                        display: "block",
                        mt: 0.25,
                        fontSize: "10px",
                        lineHeight: 1.4,
                        opacity: 0.55,
                      }}
                    >
                      {status.description}
                    </VuiTypography>
                  )}
                </VuiBox>
              </VuiBox>
            );
          })}
        </VuiBox>

        {updateStatusError && (
          <VuiBox
            display="flex"
            alignItems="center"
            gap={1}
            p={1.2}
            mt={2}
            borderRadius="md"
            sx={{
              border: "1px solid rgba(239,68,68,0.4)",
              background: "rgba(239,68,68,0.08)",
            }}
          >
            <FaExclamationCircle size={14} color="#ef4444" />

            <VuiTypography variant="caption" color="error">
              {typeof updateStatusError === "string"
                ? updateStatusError
                : updateStatusError?.message || "Failed to update room status"}
            </VuiTypography>
          </VuiBox>
        )}
      </DialogContent>

      <DialogActions sx={{ p: 2 }}>
        <VuiButton
          variant="outlined"
          color="primary"
          onClick={handleClose}
          disabled={updateStatusLoading}
        >
          Cancel
        </VuiButton>

        <VuiButton
          variant="contained"
          color="primary"
          onClick={handleSubmit}
          disabled={!resourceState || updateStatusLoading || resourceState === room?.resourceState}
        >
          {updateStatusLoading ? "Updating..." : "Update State"}
        </VuiButton>
      </DialogActions>
    </Dialog>
  );
};

export default UpdateRoomStatusDialog;
